import { deepmerge } from '@mui/utils';
import { createTheme as muiCreateTheme } from '@mui/material/styles';
import { stringEqualsIgnoreCase } from '@Components/Utilities/SandhillsUtility';

// Site Themes
import AC_Theme from '@Themes/Aircraft';
import AT_Theme from '@Themes/AuctionTime';
import AV_Theme from '@Themes/AuctionValues';
import AVT_Theme from '@Themes/AviationTrader';
import BC_Theme from '@Themes/BidCaller';
import CSM_Theme from '@Themes/CamionSuperMarket';
import CH_Theme from '@Themes/CharterHub';
import CNT_Theme from '@Themes/Controller';
import CNTEMEA_Theme from '@Themes/ControllerEMEA';
import CT_Theme from '@Themes/CraneTrader';
import EM_Theme from '@Themes/EquineMarket';
import FP_Theme from '@Themes/FarmAndPlant';
import FML_Theme from '@Themes/FarmMachineryLocator';
import FT_Theme from '@Themes/ForestryTrader';
import HS_Theme from '@Themes/HostedSite';
import LT_Theme from '@Themes/LiftsToday';
import LSM_Theme from '@Themes/LivestockMarket';
import MAT_Theme from '@Themes/Machinery';
import MBK_Theme from '@Themes/MarketBook';
import MBKBR_Theme from '@Themes/MarketBookBR';
import MDT_Theme from '@Themes/MDTrader';
import MSU_Theme from '@Themes/MotorSportsUniverse';
import NTE_Theme from '@Themes/NeedTurfEquipment';
import OFT_Theme from '@Themes/OilFieldTrader';
import OS_Theme from '@Themes/OtherStock';
import PE_Theme from '@Themes/PavingEquipment';
import PST_Theme from '@Themes/PowerSystemsToday';
import RVU_Theme from '@Themes/RVUniverse';
import RY_Theme from '@Themes/RentalYard';
import SPQ_Theme from '@Themes/SpecialtyCropEq';
import THO_Theme from '@Themes/TractorHouse';
import TT_Theme from '@Themes/TreeTrader';
import TRK_Theme from '@Themes/Truck';
import TRL_Theme from '@Themes/TruckLocator';
import TW_Theme from '@Themes/TruckWorld';
import TBSI_Theme from '@Themes/TBSI';
import UTT_Theme from '@Themes/UtilityTrailersToday';

const createTheme = (hsMuiTheme = null) => {
    try {
        return buildTheme(hsMuiTheme);
    } catch (error) {
        console.error(error);
        if (hsMuiTheme) {
            console.error('Failed to create custom site theme:');
            console.error(hsMuiTheme);
        }

        return buildTheme(null);
    }
};

const buildTheme = (hsMuiTheme) => {
    let siteName = sandhills.TradeSites.React.ComponentProps['siteName'];

    if (siteName == 'MarketBook' && sandhills.TradeSites.React.ComponentProps['topLevelDomain'] == '.com.br') {
        siteName = 'MarketBookBR';
    }

    // Get base themes for site and HS (if applicable)
    let siteBaseTheme = getSiteBaseTheme(siteName);
    let hsBaseTheme = hsMuiTheme?.baseTheme;

    // Quick fix to force mode to lowercase
    if (hsBaseTheme?.palette?.mode) {
        hsBaseTheme.palette.mode = hsBaseTheme.palette.mode.toLowerCase();
    }

    // Check Current Mode
    var siteBaseMode = siteBaseTheme?.palette?.mode;
    var hsBaseMode = hsBaseTheme?.palette?.mode;

    let baseTheme = null;
    let baseThemeInverted = null;

    // Get base theme based on current mode
    // Get inverted theme based on the opposite of the current mode
    if (hsBaseMode) {
        baseTheme = getDefaultTheme(hsBaseMode);
        baseThemeInverted = getDefaultTheme(hsBaseMode == 'dark' ? 'light' : 'dark');
    }
    else {
        baseTheme = getDefaultTheme(siteBaseMode);
        baseThemeInverted = getDefaultTheme(siteBaseMode == 'dark' ? 'light' : 'dark');
    }

    // Merge base theme with site base theme specifications
    let themeOptions = deepmerge(baseTheme, siteBaseTheme);
    let themeOptionsInverted = deepmerge(baseThemeInverted, siteBaseTheme);
    if (hsBaseTheme) {
        themeOptions = deepmerge(themeOptions, hsBaseTheme);
        themeOptionsInverted = deepmerge(themeOptionsInverted, hsBaseTheme);
    }

    // Get basic themes
    let theme = muiCreateTheme(themeOptions);
    let themeInverted = muiCreateTheme(themeOptionsInverted);

    // Merge with theme Dependencies
    let baseThemeDependent = getDefaultThemeDependent(theme);
    let siteThemeDependent = getSiteThemeDependent(siteName, theme);
    theme = deepmerge(theme, baseThemeDependent);
    theme = deepmerge(theme, siteThemeDependent);
    if (hsMuiTheme?.themeDependent) {
        theme = deepmerge(theme, hsMuiTheme?.themeDependent);
    }

    let baseThemeDependentInverted = getDefaultThemeDependent(themeInverted);
    let siteThemeDependentInverted = getSiteThemeDependent(siteName, themeInverted);
    themeInverted = deepmerge(themeInverted, baseThemeDependentInverted);
    themeInverted = deepmerge(themeInverted, siteThemeDependentInverted);
    if (hsMuiTheme?.themeDependent) {
        themeInverted = deepmerge(themeInverted, hsMuiTheme?.themeDependent);
    }

    // Final themes
    const mergedTheme = muiCreateTheme(theme);
    const mergedThemeInverted = muiCreateTheme(themeInverted);

    const result = {
        // Get final theme mode
        mainMode: mergedTheme?.palette?.mode,
        isHostedSite: siteName == 'HostedSite' ? true : false,
        // Theme options
        lightTheme: mergedTheme?.palette?.mode == 'light' ? mergedTheme : mergedThemeInverted,
        darkTheme: mergedTheme?.palette?.mode == 'dark' ? mergedTheme : mergedThemeInverted,
    };
    return result;
}

const getDefaultTheme = (defaultMode) => {
    const red = '#ce2626';
    const gray = '#4d4d4d';
    const white = '#ffffff';
    const yellow = '#FFCC01';
    const black = '#000';

    let defaultTheme = {
        palette: {
            mode: defaultMode,
            tertiary: {
                main: '#f3f3f3',
                light: '#f5f5f5',
                dark: '#aaaaaa',
                contrastText: '#666666'
            },
            auction: {
                main: red,
                contrastText: white
            },
            equipmentFacts: {
                main: yellow,
                contrastText: black
            },
            cancel: {
                main: gray
            },
            contrastDefault: {
                main: defaultMode == 'dark' ? white : '#666666'
            },
            background: {
                main: defaultMode == 'dark' ? '#222222' : '#f3f3f3'
            }
        },
        typography: {
            fontFamily: 'Arial, sans-serif',
            body1: {
                lineHeight: null,
                color: null,
            },
            body2: {
                lineHeight: 1.75,
                fontSize: '0.8125rem',
                color: defaultMode == 'dark' ? white : '#666666'
            },
            h2: {
                fontSize: '1.125rem',
                lineHeight: 1.2
            }
        }
    };

    return defaultTheme;
};

const getDefaultThemeDependent = (theme) => {
    const themeDependent = {
        components: {
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        '& .MuiFormControl-root .MuiInputLabel-root': {
                            color: theme.palette.text.primary
                        },
                        '& .MuiFormControl-root .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 'bold',
                            color: theme.palette.text.primary
                        },
                        '& .MuiFormControl-root .MuiInputLabel-root.Mui-error': {
                            color: theme.palette.error.main
                        }
                    },
                    listbox: {
                        padding: 0
                    },
                    option: {
                        borderBottom: '1px solid #cccccc'
                    }
                }
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'filled',
                },
                styleOverrides: {
                    root: {
                        '& .MuiInputLabel-root': {
                            color: theme.palette.text.primary
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 'bold',
                            color: theme.palette.text.primary
                        },
                        '& .MuiInputLabel-root.Mui-error': {
                            color: theme.palette.error.main
                        }
                    }
                },
                variants: [
                    {
                        props: { variant: 'filled', bgVariant: 'white' },
                        style: {
                            '& .MuiFilledInput-root, .MuiFilledInput-root:hover, .MuiFilledInput-root.Mui-focused': {
                                backgroundColor: theme.palette.mode === 'dark' ? null : theme.palette.common.white
                            }
                        }
                    }
                ]
            },
            MuiButton: {
                defaultProps: {
                    variant: 'contained',
                },
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        textTransform: 'none'
                    }
                },
                variants: [
                    {
                        props: { variant: 'contained', size: 'small' },
                        style: {
                            '&:hover': {
                                color: theme.palette.mode == 'dark' ? '#fff' : theme.palette.contrastDefault
                            },
                            fontSize: '12px',
                            textTransform: 'uppercase'
                        },
                        props: { variant: 'text', size: 'small' },
                        style: {
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' && theme.palette.primary.main
                            },
                        }
                    }
                ]
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        color: theme.palette.mode === 'dark' && theme.palette.primary.contrastText
                    }
                }
            },
            MuiIcon: {
                styleOverrides: {
                    root: {
                        fontSize: 'inherit',
                        color: theme.palette.contrastDefault
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'inherit'
                    }
                }
            },
            MuiPaginationItem: {
                styleOverrides: {
                    root: {
                        '&.MuiPaginationItem-previousNext': {
                            padding: '0px 10px',
                            margin: '0px 4px',
                            cursor: 'pointer',
                            border: 'none',
                            fontWeight: 'bold',
                            textDecoration: 'none',
                            color: theme.palette.primary.contrastText,
                            backgroundColor: theme.palette.primary.main
                        },
                        '&.MuiPaginationItem-previousNext:hover, &.MuiPaginationItem-previousNext:focus': {
                            backgroundColor: theme.palette.primary.hover
                        },
                        '&.MuiPaginationItem-ellipsis': {
                            display: 'none'
                        },
                        '&.MuiPaginationItem-page, &.MuiPaginationItem-firstLast': {
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.tertiary.contrastText
                        }
                    }
                }
            }
        }
    };

    return themeDependent;
};

const getSiteTheme = (siteName) => {
    switch (siteName) {
        case 'Aircraft':
            return AC_Theme;
            break;
        case 'AuctionTime':
            return AT_Theme;
            break;
        case 'AuctionValues':
            return AV_Theme;
            break;
        case 'AviationTrader':
            return AVT_Theme;
            break;
        case 'BidCaller':
            return BC_Theme;
            break;
        case 'CamionSuperMarket':
            return CSM_Theme;
            break;
        case 'CharterHub':
            return CH_Theme;
            break;
        case 'Controller':
            return CNT_Theme;
            break;
        case 'ControllerEMEA':
            return CNTEMEA_Theme;
            break;
        case 'CraneTrader':
            return CT_Theme;
            break;
        case 'EquineMarket':
            return EM_Theme;
            break;
        case 'FarmAndPlant':
            return FP_Theme;
            break;
        case 'FarmMachineryLocator':
            return FML_Theme;
            break;
        case 'ForestryTrader':
            return FT_Theme;
            break;
        case 'HostedSite':
            return HS_Theme;
            break;
        case 'LiftsToday':
            return LT_Theme;
            break;
        case 'LivestockMarket':
            return LSM_Theme;
            break;
        case 'Machinery':
            return MAT_Theme;
            break;
        case 'MarketBook':
            return MBK_Theme;
            break;
        case 'MarketBookBR':
            return MBKBR_Theme;
            break;
        case 'MDTrader':
            return MDT_Theme;
            break;
        case 'MotorSportsUniverse':
            return MSU_Theme;
            break;
        case 'NeedTurfEquipment':
            return NTE_Theme;
            break;
        case 'OilFieldTrader':
            return OFT_Theme;
            break;
        case 'OtherStock':
            return OS_Theme;
            break;
        case 'PavingEquipment':
            return PE_Theme;
            break;
        case 'PowerSystemsToday':
            return PST_Theme;
            break;
        case 'RentalYard':
            return RY_Theme;
            break;
        case 'RVUniverse':
            return RVU_Theme;
            break;
        case 'SpecialtyCropEq':
            return SPQ_Theme;
            break;
        case 'TractorHouse':
            return THO_Theme;
            break;
        case 'TreeTrader':
            return TT_Theme;
            break;
        case 'Truck':
            return TRK_Theme;
            break;
        case 'TruckWorld':
            return TW_Theme;
            break;
        case 'TruckLocator':
            return TRL_Theme;
            break;
        case 'UtilityTrailersToday':
            return UTT_Theme;
            break;
        case 'TBSI':
            return TBSI_Theme;
            break;
        default:
            return null;
            break;
    }
};

const getSiteBaseTheme = (siteName) => {
    let siteTheme = getSiteTheme(siteName);
    return siteTheme?.baseTheme();
};

const getSiteThemeDependent = (siteName, theme) => {
    let siteTheme = getSiteTheme(siteName);
    return siteTheme?.themeDependent(theme);
};

export { createTheme };